import React from "react";
import { Icon } from "@fluentui/react";
import style from "./index.module.scss";

interface TimelineEntry {
    timestamp: string;
    role: string;
    content: string;
    taskName: string | null;
}

interface TimelineData {
    entries: TimelineEntry[];
    totalCount: number;
}

interface ParsedStage {
    id: string;
    type: "user" | "planning" | "agent" | "thinking" | "outline" | "complete" | "message" | "review" | "theme" | "pptgen" | "preview";
    title: string;
    timestamp: string;
    duration?: string;
    status?: "completed" | "inProgress" | "failed";
    icon: string;
    color: string;
    details?: string[];
    children?: ParsedStage[];
    metadata?: any; // For storing additional structured data
}

const parseJsonData = (data: TimelineData): ParsedStage[] => {
    const stages: ParsedStage[] = [];
    const { entries } = data;

    entries.forEach((entry, index) => {
        try {
            // Try to parse content first to check for cardType
            let content: any;
            let cardType: string | null = null;
            try {
                content = typeof entry.content === "string" ? JSON.parse(entry.content) : entry.content;

                // Check if content has the new structure with cardType and data
                if (content && content.data) {
                    cardType = content.cardType || null;
                    content = content.data; // Extract the actual data
                }
            } catch (parseError) {
                // If JSON parsing fails, check if it's old User role format
                if (entry.role === "User") {
                    stages.push({
                        id: `stage-${index}`,
                        type: "user",
                        title: "User Request",
                        timestamp: entry.timestamp,
                        status: "completed",
                        icon: "Contact",
                        color: "#0078d4",
                        details: [typeof entry.content === "string" ? entry.content : JSON.stringify(entry.content)],
                    });
                    return;
                }
                // Otherwise treat it as a plain text message
                console.warn("Failed to parse JSON content, treating as plain text:", entry.content);
                stages.push({
                    id: `stage-${index}`,
                    type: "message",
                    title: entry.taskName || "Clippy Message",
                    timestamp: entry.timestamp,
                    status: "completed",
                    icon: "ChatBot",
                    color: "#0078d4",
                    details: [typeof entry.content === "string" ? entry.content : JSON.stringify(entry.content)],
                });
                return;
            }

            // Handle UserInput cardType - show content and files in details
            if (cardType === "UserInput" && content.content) {
                const details: string[] = [content.content];

                // Add files information if available
                if (content.files) {
                    if (Array.isArray(content.files) && content.files.length > 0) {
                        // Extract file names from array
                        const fileNames = content.files
                            .map((f: any) => {
                                // Try different possible property names
                                if (typeof f === "string") return f;
                                return f.name || f.fileName || f.Name || f.FileName || "Unknown file";
                            })
                            .filter((name: string) => name !== "Unknown file");

                        if (fileNames.length > 0) {
                            details.push(`Attached Files: ${fileNames.join(", ")}`);
                        } else {
                            details.push("Attached Files: (file details unavailable)");
                        }
                    } else if (Array.isArray(content.files) && content.files.length === 0) {
                        // Empty array - no files attached, don't add any file info
                    } else {
                        // files is not an array (might be a single file object or other structure)
                        details.push("Attached Files: (see metadata)");
                    }
                }

                stages.push({
                    id: `stage-${index}`,
                    type: "user",
                    title: "User Input",
                    timestamp: entry.timestamp,
                    status: "completed",
                    icon: "Contact",
                    color: "#0078d4",
                    details,
                });
                return;
            }

            // For old format, check role
            if (entry.role === "User") {
                stages.push({
                    id: `stage-${index}`,
                    type: "user",
                    title: "User Request",
                    timestamp: entry.timestamp,
                    status: "completed",
                    icon: "Contact",
                    color: "#0078d4",
                    details: [typeof entry.content === "string" ? entry.content : JSON.stringify(entry.content)],
                });
                return;
            }

            if (entry.role === "Clippy" || cardType) {
                // Handle based on cardType if available
                if (cardType === "TaskList" && content.tasks) {
                    // Task planning stage
                    stages.push({
                        id: `stage-${index}`,
                        type: "planning",
                        title: "Task Planning",
                        timestamp: entry.timestamp,
                        status: "completed",
                        icon: "TaskManager",
                        color: "#107c10",
                        details: content.tasks.map((task: any) => `${task.assignedAgent}: ${task.description}`),
                    });
                } else if (cardType === "Message" && content.content) {
                    // Simple message card
                    stages.push({
                        id: `stage-${index}`,
                        type: "message",
                        title: "Agent Message",
                        timestamp: entry.timestamp,
                        status: content.isCompleted ? "completed" : "inProgress",
                        icon: "ChatBot",
                        color: "#0078d4",
                        details: [content.content],
                    });
                } else if (cardType === "ReasoningProcess" || entry.taskName === "CollectInfoAgentThinking") {
                    // Reasoning/Thinking process
                    const reasoningType = content.reasoningType || "general";
                    const title = content.progressMessage?.title || "Thinking...";

                    // Check if it's a theme generation reasoning
                    if (reasoningType === "themeSnapshotType") {
                        const message = content.progressMessage;
                        const contentText = message?.content || "";
                        const messageTitle = message?.title || "";

                        // Extract image URLs from markdown format or direct snapshots array
                        const imageUrls: string[] = [];

                        // Method 1: Check if there's a direct snapshots array in content
                        if (content.snapshots && Array.isArray(content.snapshots)) {
                            imageUrls.push(...content.snapshots);
                        }

                        // Method 2: Extract from markdown format in contentText
                        if (contentText) {
                            // Support both formats: ![Image](url) and ![Image 1](url)
                            const imageRegex = /!\[Image[^\]]*\]\((https?:\/\/[^)]+)\)/g;
                            let match;
                            // eslint-disable-next-line no-cond-assign
                            while ((match = imageRegex.exec(contentText)) !== null) {
                                if (match[1] && !imageUrls.includes(match[1])) {
                                    imageUrls.push(match[1]);
                                }
                            }
                        }

                        // Only show theme generation if:
                        // 1. Title contains "Theme Generation" (real theme backgrounds), OR
                        // 2. Has multiple images (more than 1 theme)
                        // For single slide snapshots, aggregate them into PPT Generation stage
                        const isRealTheme = messageTitle.includes("Theme Generation") || imageUrls.length > 1;

                        if (isRealTheme) {
                            stages.push({
                                id: `stage-${index}`,
                                type: "theme",
                                title: "Theme Generation",
                                timestamp: entry.timestamp,
                                status: content.isCompleted ? "completed" : "inProgress",
                                icon: "ColorSolid",
                                color: "#ca5010",
                                details: [
                                    messageTitle || "Creating theme backgrounds",
                                    imageUrls.length > 0 ? `Generated ${imageUrls.length} theme(s)` : (contentText.split("\n")[0] || ""),
                                ],
                                metadata: { images: imageUrls },
                            });
                        } else {
                            // Single slide snapshot - aggregate into PPT Generation stage
                            const existingPptGen = stages.find((s) => s.type === "pptgen" && s.title === "Generating PPT Slides");
                            if (existingPptGen) {
                                // Update existing stage
                                if (existingPptGen.metadata) {
                                    existingPptGen.metadata.slideCount = (existingPptGen.metadata.slideCount || 0) + 1;
                                    existingPptGen.metadata.endTimestamp = entry.timestamp;
                                }
                            } else {
                                // Create new PPT Generation stage for slide snapshots
                                stages.push({
                                    id: `stage-pptgen-${index}`,
                                    type: "pptgen",
                                    title: "Generating PPT Slides",
                                    timestamp: entry.timestamp,
                                    status: content.isCompleted ? "completed" : "inProgress",
                                    icon: "PowerPointDocument",
                                    color: "#d13438",
                                    details: [],
                                    metadata: {
                                        slideCount: 1,
                                        startTimestamp: entry.timestamp,
                                        endTimestamp: entry.timestamp,
                                    },
                                });
                            }
                        }
                    } else if (reasoningType === "searchType") {
                        // Search/Research reasoning
                        const existingThinking = stages.find((s) => s.type === "thinking" && s.title.includes("Research"));
                        if (existingThinking) {
                            existingThinking.children = existingThinking.children || [];
                            existingThinking.children.push({
                                id: `thinking-${index}`,
                                type: "thinking",
                                title,
                                timestamp: entry.timestamp,
                                status: content.isCompleted ? "completed" : "inProgress",
                                icon: "Lightbulb",
                                color: "#8764b8",
                            });
                            // Update parent status based on children after adding
                            const allCompleted = existingThinking.children.every((child) => child.status === "completed");
                            const hasInProgress = existingThinking.children.some((child) => child.status === "inProgress");
                            if (allCompleted) {
                                existingThinking.status = "completed";
                            } else if (hasInProgress) {
                                existingThinking.status = "inProgress";
                            }
                        } else {
                            stages.push({
                                id: `stage-${index}`,
                                type: "thinking",
                                title: "Research & Analysis",
                                timestamp: entry.timestamp,
                                status: "inProgress", // Will be updated later based on children
                                icon: "SearchAndApps",
                                color: "#8764b8",
                                children: [{
                                    id: `thinking-${index}`,
                                    type: "thinking",
                                    title,
                                    timestamp: entry.timestamp,
                                    status: content.isCompleted ? "completed" : "inProgress",
                                    icon: "Lightbulb",
                                    color: "#8764b8",
                                }],
                            });
                        }
                    } else {
                        // General thinking process
                        const existingThinking = stages.find((s) => s.type === "thinking" && !s.title.includes("Research"));
                        if (existingThinking) {
                            existingThinking.children = existingThinking.children || [];
                            existingThinking.children.push({
                                id: `thinking-${index}`,
                                type: "thinking",
                                title,
                                timestamp: entry.timestamp,
                                status: content.isCompleted ? "completed" : "inProgress",
                                icon: "Lightbulb",
                                color: "#8764b8",
                            });
                        } else {
                            stages.push({
                                id: `stage-${index}`,
                                type: "thinking",
                                title: "Thinking Process",
                                timestamp: entry.timestamp,
                                status: content.isCompleted ? "completed" : "inProgress",
                                icon: "SearchAndApps",
                                color: "#8764b8",
                                children: [{
                                    id: `thinking-${index}`,
                                    type: "thinking",
                                    title,
                                    timestamp: entry.timestamp,
                                    status: content.isCompleted ? "completed" : "inProgress",
                                    icon: "Lightbulb",
                                    color: "#8764b8",
                                }],
                            });
                        }
                    }
                } else if (cardType === "OutlineCard" || content.pageNum !== undefined) {
                    // Outline generation
                    const existingOutline = stages.find((s) => s.type === "outline");
                    if (existingOutline) {
                        existingOutline.children = existingOutline.children || [];
                        existingOutline.children.push({
                            id: `slide-${index}`,
                            type: "outline",
                            title: `Slide ${content.pageNum}: ${content.title || "Untitled"}`,
                            timestamp: entry.timestamp,
                            status: "completed",
                            icon: "PageList",
                            color: "#ca5010",
                        });
                    } else {
                        stages.push({
                            id: `stage-${index}`,
                            type: "outline",
                            title: "Creating Slides",
                            timestamp: entry.timestamp,
                            status: "inProgress",
                            icon: "PageHeaderEdit",
                            color: "#ca5010",
                            children: [{
                                id: `slide-${index}`,
                                type: "outline",
                                title: `Slide ${content.pageNum}: ${content.title || "Untitled"}`,
                                timestamp: entry.timestamp,
                                status: "completed",
                                icon: "PageList",
                                color: "#ca5010",
                            }],
                        });
                    }
                } else if (content.jsonResultText || content.fidelityScore !== undefined) {
                    // PPT Review Report
                    try {
                        // 尝试从 jsonResultText 解析，如果不存在则直接使用 content
                        let reviewData = content;
                        if (content.jsonResultText) {
                            reviewData = JSON.parse(content.jsonResultText);
                        }

                        // 合并 content 的顶层字段（新结构）到 reviewData
                        const mergedData = {
                            ...reviewData,
                            // 如果 content 有这些字段，使用 content 的值（新结构优先）
                            fidelityScore: content.fidelityScore !== undefined ? content.fidelityScore : reviewData.fidelityScore,
                            fidelityReason: content.fidelityReason || reviewData.fidelityReason,
                            fidelityStrengths: content.fidelityStrengths || reviewData.fidelityStrengths,
                            fidelityWeaknesses: content.fidelityWeaknesses || reviewData.fidelityWeaknesses,
                            aestheticsScore: content.aestheticsScore !== undefined ? content.aestheticsScore : reviewData.aestheticsScore,
                            aestheticsReason: content.aestheticsReason || reviewData.aestheticsReason,
                            aestheticsStrengths: content.aestheticsStrengths || reviewData.aestheticsStrengths,
                            aestheticsWeaknesses: content.aestheticsWeaknesses || reviewData.aestheticsWeaknesses,
                            narrativeScore: content.narrativeScore !== undefined ? content.narrativeScore : reviewData.narrativeScore,
                            narrativeReason: content.narrativeReason || reviewData.narrativeReason,
                            narrativeStrengths: content.narrativeStrengths || reviewData.narrativeStrengths,
                            narrativeWeaknesses: content.narrativeWeaknesses || reviewData.narrativeWeaknesses,
                            consistencyScore: content.consistencyScore !== undefined ? content.consistencyScore : reviewData.consistencyScore,
                            consistencyReason: content.consistencyReason || reviewData.consistencyReason,
                            consistencyStrengths: content.consistencyStrengths || reviewData.consistencyStrengths,
                            consistencyWeaknesses: content.consistencyWeaknesses || reviewData.consistencyWeaknesses,
                        };

                        stages.push({
                            id: `stage-${index}`,
                            type: "review",
                            title: "PPT Review Report",
                            timestamp: entry.timestamp,
                            status: "completed",
                            icon: "ReviewSolid",
                            color: "#8764b8",
                            metadata: mergedData,
                            details: [], // No simple text details, show full review UI
                        });
                    } catch (e) {
                        console.warn("Failed to parse review report:", e);
                    }
                } else if (cardType === "PptPreview" || cardType === "SourcePreview") {
                    // PPT Preview or Source Preview - Show preview images
                    const images: string[] = [];

                    // Extract images from various possible fields
                    if (content.snapshots && Array.isArray(content.snapshots)) {
                        images.push(...content.snapshots);
                    } else if (content.images && Array.isArray(content.images)) {
                        images.push(...content.images);
                    } else if (content.imageUrls && Array.isArray(content.imageUrls)) {
                        images.push(...content.imageUrls);
                    }

                    // Only create stage if there are images to show
                    if (images.length > 0) {
                        const details: string[] = [];

                        // Add title or description if available
                        if (content.title) {
                            details.push(content.title);
                        }
                        if (content.description) {
                            details.push(content.description);
                        }
                        if (content.message) {
                            details.push(content.message);
                        }

                        // Add image count
                        details.push(`Images: ${images.length}`);

                        stages.push({
                            id: `stage-${index}`,
                            type: "preview",
                            title: cardType === "PptPreview" ? "PPT Preview" : "Source Preview",
                            timestamp: entry.timestamp,
                            status: content.isCompleted ? "completed" : "inProgress",
                            icon: "Photo2",
                            color: "#8764b8",
                            details: details.length > 0 ? details : undefined,
                            metadata: {
                                images,
                            },
                        });
                    }
                } else if (content.status === "COMPLETED" && content.snapshots) {
                    // PPT Generation Complete - Show slide snapshots
                    const details = [
                        `Progress: ${content.progress || 100}%`,
                        `Slides: ${content.snapshots.length}`,
                        `File ID: ${content.fileId || "N/A"}`,
                    ];

                    // Add progress messages if available
                    if (content.progressMessages && content.progressMessages.length > 0) {
                        content.progressMessages.forEach((msg: any) => {
                            if (msg.title && msg.content) {
                                details.push(`${msg.title}: ${msg.content}`);
                            }
                        });
                    }

                    stages.push({
                        id: `stage-${index}`,
                        type: "pptgen",
                        title: `PPT Generated: ${content.fileTitle || "Presentation"}`,
                        timestamp: entry.timestamp,
                        status: "completed",
                        icon: "PowerPointDocument",
                        color: "#d13438",
                        details,
                        metadata: {
                            workflowId: content.workflowId,
                            // Don't save snapshots for PPT Generated - no images should be shown
                        },
                    });
                } else if (content.statusCode) {
                    // Completion
                    stages.push({
                        id: `stage-${index}`,
                        type: "complete",
                        title: "Session Complete",
                        timestamp: entry.timestamp,
                        status: content.statusCode === 200 ? "completed" : "failed",
                        icon: "CompletedSolid",
                        color: "#107c10",
                        details: [`Status: ${content.statusCode}`],
                    });
                } else if (content.content && !content.tasks && !content.pageNum) {
                    // Agent execution
                    const agentName = entry.taskName || "Agent";
                    const existingAgent = stages.find((s) => s.title.includes(agentName));
                    if (existingAgent) {
                        existingAgent.status = content.isCompleted ? "completed" : "inProgress";
                    } else {
                        stages.push({
                            id: `stage-${index}`,
                            type: "agent",
                            title: agentName,
                            timestamp: entry.timestamp,
                            status: content.isCompleted ? "completed" : "inProgress",
                            icon: "Robot",
                            color: "#f7630c",
                            details: [content.content],
                        });
                    }
                }
            }
        } catch (e) {
            // Skip entries that can't be parsed
            console.warn("Failed to parse entry:", entry, e);
        }
    });

    // Update parent stages status based on children completion
    stages.forEach((stage) => {
        if (stage.children && stage.children.length > 0) {
            // Check if all children are completed
            const allCompleted = stage.children.every((child) => child.status === "completed");
            const hasInProgress = stage.children.some((child) => child.status === "inProgress");

            if (allCompleted) {
                // All children completed -> parent completed
                stage.status = "completed";
            } else if (hasInProgress) {
                // At least one child in progress -> parent in progress
                stage.status = "inProgress";
            } else {
                // No children in progress and not all completed (some may be failed or undefined)
                // Keep parent's current status or mark as completed
                stage.status = stage.status || "completed";
            }
        }
    });

    // Calculate durations and finalize PPT Generation stages
    stages.forEach((stage) => {
        if (stage.type === "pptgen" && stage.title === "Generating PPT Slides" && stage.metadata) {
            // Calculate duration for aggregated PPT Generation
            const start = new Date(stage.metadata.startTimestamp);
            const end = new Date(stage.metadata.endTimestamp);
            const diffMs = end.getTime() - start.getTime();
            const diffSec = Math.round(diffMs / 1000);
            stage.duration = `${diffSec}s`;
            stage.details = [`Generated ${stage.metadata.slideCount} slide(s)`];
        }
    });

    // Calculate durations for other stages
    for (let i = 0; i < stages.length - 1; i++) {
        if (!stages[i].duration) {
            const start = new Date(stages[i].timestamp);
            const end = new Date(stages[i + 1].timestamp);
            const diffMs = end.getTime() - start.getTime();
            const diffSec = Math.round(diffMs / 1000);
            stages[i].duration = `${diffSec}s`;
        }
    }

    return stages;
};

const renderReviewDetails = (reviewData: any) => {
    // 支持两种数据结构：
    // 1. 旧结构：Details.scores 中包含 Score、Strengths、Weaknesses
    // 2. 新结构：顶层字段如 fidelityScore、fidelityReason、fidelityStrengths 等
    const scores = reviewData.Details?.scores || {};

    // 检查是否是新结构（顶层有 fidelityScore 等字段）
    const hasNewStructure = reviewData.fidelityScore !== undefined;

    return (
        <div className={style.reviewContent}>
            {hasNewStructure ? (
                <div className={style.scoresSection}>
                    <h5>Evaluation Scores</h5>
                    {["Fidelity", "Aesthetics", "Narrative", "Consistency"].map((category) => {
                        const lowerCategory = category.toLowerCase();
                        const score = reviewData[`${lowerCategory}Score`];
                        const reason = reviewData[`${lowerCategory}Reason`];
                        const strengths = reviewData[`${lowerCategory}Strengths`] || [];
                        const weaknesses = reviewData[`${lowerCategory}Weaknesses`] || [];

                        if (score === undefined) return null;

                        return (
                            <div key={category} className={style.scoreCategory}>
                                {/* Score bar */}
                                <div className={style.scoreItem}>
                                    <span className={style.scoreName}>
                                        {category}
                                        :
                                    </span>
                                    <span className={style.scoreValue}>
                                        {score || "N/A"}
                                    </span>
                                    <div className={style.scoreBar}>
                                        <div
                                            className={style.scoreBarFill}
                                            style={{
                                                width: `${score || 0}%`,
                                                backgroundColor: score >= 70 ? "#107c10" : score >= 50 ? "#f7630c" : "#d13438",
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Detailed analysis under each score */}
                                <div className={style.scoreDetails}>
                                    {reason && (
                                        <div className={style.reasonSection}>
                                            <strong>Reason:</strong>
                                            {" "}
                                            {reason}
                                        </div>
                                    )}
                                    {strengths.length > 0 && (
                                        <div className={style.strengthsWeaknesses}>
                                            <strong>✓ Strengths:</strong>
                                            <ul>
                                                {strengths.map((item: string) => (
                                                    <li key={`${category}-strength-${item}`}>
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {weaknesses.length > 0 && (
                                        <div className={style.strengthsWeaknesses}>
                                            <strong>✗ Weaknesses:</strong>
                                            <ul>
                                                {weaknesses.map((item: string) => (
                                                    <li key={`${category}-weakness-${item}`}>
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : Object.keys(scores).length > 0 ? (
                <div className={style.scoresSection}>
                    <h5>Evaluation Scores</h5>
                    {Object.entries(scores).map(([key, value]: [string, any]) => {
                        const strengths = (value as any).Strengths || [];
                        const weaknesses = (value as any).Weaknesses || [];
                        const reason = (value as any).Reason || "";

                        return (
                            <div key={key} className={style.scoreCategory}>
                                {/* Score bar */}
                                <div className={style.scoreItem}>
                                    <span className={style.scoreName}>
                                        {key}
                                        :
                                    </span>
                                    <span className={style.scoreValue}>
                                        {value.Score || "N/A"}
                                    </span>
                                    <div className={style.scoreBar}>
                                        <div
                                            className={style.scoreBarFill}
                                            style={{
                                                width: `${value.Score || 0}%`,
                                                backgroundColor: value.Score >= 70 ? "#107c10" : value.Score >= 50 ? "#f7630c" : "#d13438",
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Detailed analysis under each score */}
                                <div className={style.scoreDetails}>
                                    {reason && (
                                        <div className={style.reasonSection}>
                                            <strong>Reason:</strong>
                                            {" "}
                                            {reason}
                                        </div>
                                    )}
                                    {strengths.length > 0 && (
                                        <div className={style.strengthsWeaknesses}>
                                            <strong>✓ Strengths:</strong>
                                            <ul>
                                                {strengths.map((item: string) => (
                                                    <li key={`${key}-strength-${item}`}>
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {weaknesses.length > 0 && (
                                        <div className={style.strengthsWeaknesses}>
                                            <strong>✗ Weaknesses:</strong>
                                            <ul>
                                                {weaknesses.map((item: string) => (
                                                    <li key={`${key}-weakness-${item}`}>
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div>Review analysis completed (no scores available)</div>
            )}
        </div>
    );
};

const TimelineStage: React.FC<{ stage: ParsedStage; isLast: boolean }> = ({ stage, isLast }) => {
    const formatTime = (timestamp: string) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    };

    const hasChildren = stage.children && stage.children.length > 0;
    const hasDetails = stage.details && stage.details.length > 0;

    return (
        <div className={style.timelineStage}>
            <div className={style.timelineConnector}>
                <div
                    className={style.timelineIcon}
                    style={{ backgroundColor: stage.color }}
                >
                    <Icon iconName={stage.icon} styles={{ root: { color: "white", fontSize: 16 } }} />
                </div>
                {!isLast && <div className={style.timelineLine} />}
            </div>
            <div className={style.timelineContent}>
                <div
                    className={style.stageCard}
                    style={{ borderLeft: `4px solid ${stage.color}` }}
                >
                    <div className={style.stageHeader}>
                        <div className={style.stageTitle}>
                            <span className={style.stageTitleText}>{stage.title}</span>
                        </div>
                        <div className={style.stageTimestamp}>
                            <span>{formatTime(stage.timestamp)}</span>
                            {stage.duration && <span className={style.duration}>{stage.duration}</span>}
                        </div>
                    </div>
                    {hasDetails && (
                        <div className={style.stageDetails}>
                            {stage.details!.map((detail) => {
                                // 在 File ID 之前插入 Workflow ID
                                const isFileIdLine = detail.startsWith("File ID:");
                                const shouldShowWorkflowIdBefore = isFileIdLine && stage.type === "pptgen" && stage.metadata?.workflowId;

                                return (
                                    <React.Fragment key={`detail-${stage.id}-${detail.substring(0, 30)}`}>
                                        {shouldShowWorkflowIdBefore && (
                                            <div className={style.detailItem}>
                                                <span>
                                                    Workflow ID:
                                                    {" "}
                                                    {stage.metadata.workflowId}
                                                </span>
                                                <button
                                                    className={style.detailButton}
                                                    onClick={() => window.open(`/workflow/detail?workflowId=${stage.metadata.workflowId}`, "_blank")}
                                                    type="button"
                                                >
                                                    Detail
                                                </button>
                                            </div>
                                        )}
                                        <div className={style.detailItem}>
                                            {detail}
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    )}
                    {hasChildren && (
                        <div className={style.stageChildren}>
                            {stage.children!.map((child) => (
                                <div key={child.id} className={style.childItem}>
                                    <Icon iconName={child.icon} styles={{ root: { color: child.color, marginRight: 8 } }} />
                                    <span>{child.title}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {stage.type === "review" && stage.metadata && (
                        <div className={style.reviewDetails}>
                            {renderReviewDetails(stage.metadata)}
                        </div>
                    )}
                    {stage.type === "theme" && stage.metadata?.images && (
                        <div className={style.imagePreview}>
                            {stage.metadata.images.map((url: string) => (
                                <img key={url} src={url} alt="Theme preview" className={style.previewImage} />
                            ))}
                        </div>
                    )}
                    {stage.type === "preview" && stage.metadata?.images && Array.isArray(stage.metadata.images) && stage.metadata.images.length > 0 && (
                        <div className={style.imagePreview}>
                            {stage.metadata.images.map((url: string) => (
                                <img key={url} src={url} alt="Preview" className={style.previewImage} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const TimelineView: React.FC<{ data: TimelineData }> = ({ data }) => {
    const stages = parseJsonData(data);

    return (
        <div className={style.timelineView}>
            <div className={style.timelineHeader}>
                <h4>Session Timeline</h4>
                <span className={style.totalCount}>
                    {data.totalCount}
                    {" "}
                    events
                </span>
            </div>
            <div className={style.timelineContainer}>
                {stages.map((stage, index) => (
                    <TimelineStage
                        key={stage.id}
                        stage={stage}
                        isLast={index === stages.length - 1}
                    />
                ))}
            </div>
        </div>
    );
};
